import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ProductPlanCard from "./ProductPlanCard";
import { X, CheckCircle } from "lucide-react";
import { getProductDetailButtonName, getProductDetailIcon } from "./common";
import { useNavigate } from "react-router-dom";
import { Product } from "../../../shared/interfaces/model/product.interface";
import RequestQuoteModal from "./RequestQuoteModal";

const PricingPlans = ({ selectedProduct, tierBought, product }: any) => {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans2, setPlans2] = useState<any[]>([]);
  const navigate = useNavigate();
  const [confirmModal, setConfirmModal] = useState(false);
  const [isConfirmationChecked, setIsConfirmationChecked] = useState(false);
  const [requestQuoteModal, setRequestQuoteModal] = useState(false);
  const [showPurchaseButton, setShowPurchaseButton] = useState(false);
  useEffect(() => {
    const isTenantUser = localStorage.getItem("isTenantUser");
    setShowPurchaseButton(isTenantUser !== "true");
  }, []);



  useEffect(() => {
    if (selectedProduct) {
      const d: any[] = []
      Object.keys(selectedProduct.pricing).forEach((key: any) => {
        const price = selectedProduct.pricing[key];
        const v: any = {};
        v.name = key;
        v.price = price;
        v.icon = getProductDetailIcon(key);
        v.features = selectedProduct.features;
        v.tierFeatures = selectedProduct.tierFeatures;
        v.cta = getProductDetailButtonName(key);
        v.iconColor = selectedProduct.iconColor;
        v.description = selectedProduct.description;
        d.push(v);
      });
      setPlans2(d);
    }
  }, [selectedProduct])

  const handleButtonClick = (status: string) => {
    setSelectedPlan(status);
    if (status === "Enterprise") {
      setRequestQuoteModal(true);
    } else {
      setConfirmModal(true);
    }
  }

  const handleCloseModal = () => {
    setConfirmModal(false);
    setIsConfirmationChecked(false);
  }

  const goToCheckout = () => {
    if (!isConfirmationChecked) return;

    const basicInfo: Product = {
      id: selectedProduct.id,
      name: selectedProduct.name,
      desc: selectedPlan ?? "",
      appUrl: selectedProduct.appUrl,
      appCode: selectedProduct.appCode,
      created_at: new Date(),
      price: selectedPlan ? selectedProduct.pricing[selectedPlan as keyof typeof selectedProduct.pricing] : 0,
    }

    const productDetails = {
      productAppCode: selectedProduct.appCode,
      expireDate: new Date().toString(),
    }

    navigate(`/private/checkoutPage/${basicInfo.appCode}`, {
      state: { product: basicInfo, paidProductInfo: productDetails },
    });
  };


  const handleRequestQuoteClose = () => {
    setRequestQuoteModal(false);
  }


  return (
    <div className="max-w-6xl mx-auto p-8 w-full">
      <motion.div
        className="grid md:grid-cols-3 gap-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ staggerChildren: 0.1 }}
      >
        <AnimatePresence>
          {plans2.map((plan) => (
            <ProductPlanCard
              key={plan.name}
              plan={plan}
              isSelected={selectedPlan === plan.name}
              onSelect={setSelectedPlan}
              hideAction={!showPurchaseButton}
              onButtonClick={handleButtonClick}
              tierBought={tierBought}
              product={product}
            />
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {confirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={handleCloseModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Accept terms and conditions
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 dark:text-gray-300 mb-4 overflow-y-auto max-h-96">
                  <div className="max-w-4xl mx-auto px-4 py-8">
                    <h1 className="text-3xl font-bold mb-8">
                      Terms of Reference for Nexbot Online Service Purchase
                    </h1>

                    <div className="prose prose-lg">
                      <section className="mb-8">
                        <p className="mb-2">
                          This Terms of Reference (hereinafter “Terms” or “Agreement”)
                          governs the purchase and use of the Nexbot online service (the
                          “Service”) provided by Nexum S.R.L. (the “Provider”) to the end
                          user (the “User”). By subscribing to or using the Service, the
                          User acknowledges and agrees to be bound by these Terms. These
                          Terms are drafted in accordance with applicable Italian e-commerce
                          regulations and best practices for digital service agreements.
                        </p>
                      </section>
                      <section>
                        <h3>1. Subscription Tiers and Pricing</h3>
                        <p className="mb-2">
                          Nexbot is offered under a tiered subscription model. The available
                          tiers and their standard prices are as follows:
                        </p>
                        <ul>
                          <li>Standard: €9.99 per month.</li>
                          <li>Premium: €19.99 per month.</li>
                          <li>
                            Enterprise: Custom pricing (available upon request, requiring
                            direct contact with our sales team at salesnexum@nexum-ai.com).
                          </li>
                        </ul>
                        <p>
                          All prices are in Euros and include any applicable taxes unless
                          stated otherwise. Pricing is subject to change over time at the
                          discretion of Nexum S.R.L. The Provider may modify subscription
                          fees or introduce new tiers, and will provide notice to Users in
                          accordance with legal requirements before changes take effect.
                          Continued use of the Service after a price change constitutes
                          acceptance of the new pricing. Enterprise tier pricing and terms
                          will be defined in a separate agreement or order form negotiated
                          with the User.
                        </p>
                      </section>
                      <section>
                        <h3>2. Refund Policy</h3>
                        <p className="mb-2">
                          Subscriptions are billed in advance and, by default, service
                          termination or cancellation by the User does not entitle the User
                          to any refund for the remaining subscription period. The Service
                          is provided on a time-period access basis (monthly or as otherwise
                          agreed), and Users are responsible for managing their subscription
                          duration.
                        </p>
                        <p className="mb-2">
                          Exception – 48 Hour Initial Use Refund: If a new User experiences
                          an inability to access or use the Service’s core functions due to
                          technical issues attributable to Nexum within the first
                          forty-eight (48) hours of the initial subscription purchase, the
                          User may request a cancellation and refund. To qualify for this
                          refund, the User must: (a) notify Nexum S.R.L. in writing (e.g.
                          via support contact or email) within the first 48 hours of
                          activation, and (b) provide reasonable evidence of the inability
                          to use the Service (for example, error messages or support tickets
                          detailing the issue). Upon verification of these conditions by
                          Nexum S.R.L., the User will be eligible for a refund of the
                          subscription fee paid for that month. This initial-use refund is
                          provided as a goodwill policy and does not affect any statutory
                          rights.
                        </p>
                        <p>
                          Except as expressly provided above, no other refunds or credits
                          will be issued for partial use of the Service, downgrades, or
                          early termination. Nothing in this Refund Policy affects rights
                          that cannot be excluded or limited under applicable law.
                        </p>
                      </section>

                      <section>
                        <h3>3. Jurisdiction and Dispute Resolution </h3>
                        <p className="mb-2">
                          This Agreement and any disputes or claims arising from it are
                          governed by the laws of Italy, without regard to its conflict of
                          law principles. The parties agree that the exclusive jurisdiction
                          for all legal proceedings relating to or arising out of this
                          Agreement shall be the competent courts of Rome, Italy. The User
                          and Nexum S.R.L. consent to the personal jurisdiction of such
                          courts.
                        </p>
                        <p className="mb-2">
                          If the User is a consumer resident outside Italy, mandatory
                          provisions of the law of the country where the consumer resides
                          may apply for their benefit, but in all cases Italian law will
                          govern the contractual terms to the maximum extent permitted. In
                          the event of any dispute, the User is encouraged to first contact
                          Nexum S.R.L. to seek an amicable resolution. All negotiations and
                          dispute resolution communications shall be conducted in good
                          faith. This clause does not limit either party’s right to seek
                          interim or preliminary relief (such as an injunction) in any
                          competent court to protect its rights and interests.
                        </p>
                      </section>

                      <section>
                        <h3>4. Intellectual Property Rights</h3>
                        <p className="mb-2">
                          All intellectual property rights in and to the Service, including
                          but not limited to the software, algorithms, AI functionalities,
                          design, logos, content, and any creative elements, are and shall
                          remain the exclusive property of Nexum S.R.L.. This includes any
                          content or components protected under copyright laws and any
                          rights managed by the Italian Society of Authors and Publishers
                          (SIAE), as applicable. Nothing in this Agreement transfers any
                          intellectual property rights to the User. The User is granted a
                          limited, non-exclusive, non-transferable, revocable license to
                          access and use the Service for the duration of an active
                          subscription, solely in accordance with these Terms.
                        </p>
                        <p className="mb-2">
                          Prohibited Uses: The User shall not, and shall not permit any
                          third party to, do any of the following:
                        </p>
                        <ul>
                          <li>
                            Copy, reproduce, distribute, or publish any portion of the
                            Service or its content (except as expressly allowed by the
                            Service’s intended functionalities).
                          </li>
                          <li>
                            Modify, adapt, translate, reverse engineer, decompile,
                            disassemble, or create derivative works based on the Service or
                            any part thereof.
                          </li>
                          <li>
                            Remove or alter any proprietary notices or labels on the Service
                            (including copyright and trademark notices).
                          </li>
                          <li>
                            Resell, sublicense, rent, or otherwise transfer the Service or
                            access to it to any third party, unless expressly authorized in
                            writing by Nexum S.R.L.
                          </li>
                        </ul>
                        <p className="mb-2">
                          All content generated or provided by the Service is for the User’s
                          personal or internal business use (according to the subscription
                          scope) and remains subject to Nexum S.R.L.’s intellectual property
                          rights. Any rights not expressly granted to the User under these
                          Terms are reserved by Nexum S.R.L. Violation of the intellectual
                          property provisions may result in immediate suspension or
                          termination of access to the Service, without prejudice to any
                          legal remedies available to Nexum S.R.L.
                        </p>
                      </section>
                      <section>
                        <h3>5. User Responsibilities and Usage Limitations</h3>
                        <p className="mb-2">
                          By using the Service, the User agrees to comply with the following
                          responsibilities and usage limitations associated with their
                          chosen subscription tier:
                        </p>
                        <p>
                          5.1 Proper Use: The User shall use the Service in accordance with
                          these Terms, the documentation, and any applicable laws and
                          regulations. The Service must not be used for any unlawful,
                          fraudulent, or abusive purpose. The User is solely responsible for
                          the content of their use of the Service (such as queries made to
                          the AI or data input) and for ensuring that such use does not
                          violate any third-party rights or applicable laws (including
                          intellectual property rights or data protection laws).
                        </p>
                        <p>
                          5.2 Subscription Tier Limits: Each subscription tier may carry
                          specific usage limits or feature restrictions. For example, tiers
                          may differ in the number of user accounts, volume of requests or
                          transactions, access to advanced features, or level of support
                          provided. The User agrees to abide by the limits of the chosen
                          tier. Standard and Premium tier Users are entitled to the
                          predefined services and usage quotas as described on the Nexbot
                          website or service description at the time of purchase. Enterprise
                          tier Users will have usage parameters defined in their custom
                          agreement. Exceeding the permitted usage of a tier without an
                          appropriate upgrade or prior authorization is prohibited and may
                          result in additional charges, suspension of service, or
                          downgrading of the account to an appropriate tier.
                        </p>
                        <p>
                          5.3 Changes to Usage Limits: The usage limitations and features of
                          each tier are subject to change over time. Nexum S.R.L. reserves
                          the right to modify tier features, quotas, or other parameters to
                          improve the Service or adapt to operational needs. Any material
                          changes to usage limits or User obligations will be communicated
                          to the User (for example, via email or an update on the official
                          website) in advance, in accordance with applicable regulations.
                          Continued use of the Service after such changes take effect
                          constitutes acceptance of the new terms.
                        </p>
                        <p>
                          5.4 Account Security: The User is responsible for maintaining the
                          confidentiality and security of their account credentials (login
                          IDs, passwords, API keys, etc.). The account may only be used by
                          the authorized individual or entity that subscribed, and if the
                          subscription tier permits a certain number of authorized users,
                          usage is limited to those users. Sharing of login credentials
                          beyond what is allowed for the User’s tier is strictly prohibited.
                          The User must promptly notify Nexum S.R.L. of any unauthorized
                          access or suspected security breaches related to the Service.
                        </p>
                        <p>
                          5.5 Compliance and Cooperation: The User agrees to use the Service
                          in a manner that does not overload or materially interfere with
                          the integrity or operation of the Service. If Nexum S.R.L. detects
                          abnormal usage patterns that suggest misuse of the Service or a
                          violation of the tier limitations (for example, automated
                          scraping, excessive calls, or activities that compromise service
                          performance), Nexum S.R.L. may contact the User for clarification
                          or take appropriate measures, including temporary rate limiting or
                          suspension, to protect the Service. The User shall cooperate with
                          Nexum S.R.L. in investigating and resolving any such issues.
                        </p>
                      </section>

                      <section>
                        <h3>6. Service Availability and Disclaimer of Warranty</h3>
                        <p>
                          6.1 Availability: Nexum S.R.L. endeavors to keep the Nexbot
                          Service available and operational for Users as continuously as
                          possible. However, no uptime or availability guarantee is given
                          under this Agreement. The Service may occasionally be interrupted
                          or limited due to scheduled maintenance, upgrades, emergency
                          repairs, telecommunications or network failures, or other events
                          beyond the Provider’s control. Nexum S.R.L. will attempt to
                          perform scheduled maintenance during off-peak hours and, when
                          feasible, will provide advance notice to Users of any significant
                          downtime.
                        </p>
                        <p>
                          6.2 No Liability for Outages: The User acknowledges that service
                          outages, delays, or errors may occur and agrees that Nexum S.R.L.
                          shall not be held liable for any loss, damage, or inconvenience
                          resulting from the unavailability or impaired functioning of the
                          Service, regardless of cause. This includes, but is not limited
                          to, any loss of data or business opportunity arising from
                          downtime. In no event will Nexum S.R.L. be responsible for
                          compensating the User for service interruptions or failures,
                          except where expressly required by law. The Service is provided on
                          an “as is” and “as available” basis, and all warranties, whether
                          express or implied, including any implied warranties of
                          merchantability, fitness for a particular purpose, or
                          non-infringement, are disclaimed to the maximum extent permitted
                          by law.
                        </p>

                        <p>
                          6.3 No Guarantee of Results: Nexbot’s AI functionalities and
                          outputs are provided without any guarantee of accuracy,
                          completeness, or suitability for the User’s particular needs.
                          While Nexum S.R.L. strives to ensure high quality and reliability,
                          the User understands that use of the Service is at their own risk.
                          Nexum S.R.L. does not warrant that the Service will meet all
                          expectations or that it will achieve any specific results intended
                          by the User.
                        </p>
                        <p>
                          6.4 Limitation of Liability: To the extent permitted under
                          applicable law, in no case shall Nexum S.R.L. be liable for
                          indirect, incidental, consequential, special, or punitive damages,
                          or for loss of profits or revenues, arising out of or in
                          connection with the use or inability to use the Service. The total
                          aggregate liability of Nexum S.R.L. for any claims under this
                          Agreement shall not exceed the amount of fees actually paid by the
                          User for the Service in the six (6) months preceding the event
                          giving rise to the claim. This clause does not exclude or limit
                          Nexum S.R.L.’s liability in cases of gross negligence, willful
                          misconduct, or other liabilities that cannot be limited under law.
                        </p>
                      </section>

                      <section>
                        <h3>7. Data Privacy and GDPR Compliance</h3>
                        <p>
                          7.1 Compliance with Data Protection Laws: Nexum S.R.L. is
                          committed to protecting User privacy and personal data in
                          accordance with the EU General Data Protection Regulation
                          (Regulation (EU) 2016/679, GDPR) and applicable Italian data
                          protection laws (such as Legislative Decree 196/2003, as amended,
                          “Codice Privacy”). By using the Service, the User acknowledges
                          that certain personal data will be processed by Nexum S.R.L. and
                          its service providers for the purpose of account administration,
                          Service delivery, and customer support. All personal data is
                          handled as detailed in our Privacy Policy, which is incorporated
                          by reference into these Terms. The Privacy Policy provides
                          information on data processing purposes, the types of personal
                          data collected, data retention periods, and the rights of data
                          subjects.
                        </p>
                        <p>
                          7.2 Payment Data and Security: All payment transactions for the
                          Service are processed through Stripe, a third-party payment
                          processor. Nexum S.R.L. does not store or have access to Users’
                          sensitive payment information (such as full credit card numbers or
                          security codes) on its own servers. Stripe handles payment data in
                          compliance with strict security standards (PCI-DSS Level 1
                          certification) to ensure the protection of financial information.
                          When the User provides payment details to subscribe to Nexbot,
                          those details are transmitted directly to Stripe over encrypted
                          connections. Nexum S.R.L. receives from Stripe only the
                          information necessary to confirm the payment (such as a payment
                          confirmation, last four digits of the card for reference, card
                          type, and the User’s billing name and address) and to manage the
                          User’s subscription status. By agreeing to these Terms and
                          submitting payment information, the User consents to the
                          processing of their payment data by Stripe, subject to Stripe’s
                          services agreement and privacy policy.
                        </p>
                        <p>
                          7.3 Personal Data Handling: Nexum S.R.L. limits the personal data
                          it collects to what is necessary for providing the Service
                          effectively (for example, name, contact information, account
                          credentials, and usage data). Such data is used for account
                          creation, authentication, delivering AI responses, improving
                          Service performance, billing, and communicating with the User
                          about the Service. Nexum S.R.L. will not sell or rent the User’s
                          personal data to third parties. Personal data may be shared with
                          sub-processors or partners of Nexum S.R.L. strictly for the
                          purposes of operating the Service (e.g., cloud hosting providers
                          or customer support tools), and always under data processing
                          agreements that ensure GDPR compliance.
                        </p>

                        <p>
                          7.4 Users’ Rights: In accordance with GDPR, Users have the right
                          to access their personal data held by Nexum S.R.L., and to request
                          correction or deletion of such data. Users also have the right to
                          data portability, to restrict or object to processing in certain
                          circumstances, and to withdraw any consent given for data
                          processing (without affecting the lawfulness of processing based
                          on consent before its withdrawal). To exercise these rights, Users
                          may contact Nexum S.R.L. at the contact information provided in
                          the Privacy Policy or at the registered office address. Nexum
                          S.R.L. will respond to such requests within the timeframes
                          established by law. If a User believes their data has been handled
                          in violation of applicable law, they have the right to lodge a
                          complaint with the Italian data protection authority (Garante per
                          la Protezione dei Dati Personali) or with the supervisory
                          authority in their EU member state of residence.
                        </p>

                        <p>
                          7.5 Data Security: Nexum S.R.L. implements appropriate technical
                          and organizational measures to protect personal data and Service
                          content against unauthorized access, alteration, disclosure, or
                          destruction. However, the User understands that no method of
                          transmission or storage is completely secure, and Nexum S.R.L.
                          cannot guarantee absolute security. In the event of a data breach
                          involving the User’s personal data, Nexum S.R.L. will follow all
                          applicable breach notification laws, including informing Users and
                          relevant authorities as required.
                        </p>
                        <p>
                          7.6 Retention and Deletion: Personal data collected in connection
                          with the Service will be retained for as long as necessary to
                          fulfill the purposes outlined in these Terms and the Privacy
                          Policy, or as required by law (for example, record-keeping
                          obligations for financial transactions). When personal data is no
                          longer needed, Nexum S.R.L. will securely delete or anonymize it
                          in accordance with best practices and applicable regulations.
                        </p>
                      </section>

                      <section>
                        <h3>8. Miscellaneous Provisions </h3>
                        <p>
                          8.1 Amendments: Nexum S.R.L. reserves the right to amend or update
                          these Terms from time to time, for example to reflect changes in
                          the law, the Service features, or business practices. When changes
                          occur, Nexum S.R.L. will notify Users by appropriate means (such
                          as via email or a notice on the Service interface), and will
                          indicate the effective date of the updated Terms. If the User does
                          not agree to the revised Terms, they must discontinue use of the
                          Service and may cancel their subscription. Continued use of the
                          Service after the effective date of the updated Terms constitutes
                          acceptance of the changes.
                        </p>
                        <p>
                          8.2 No Waiver: The failure of Nexum S.R.L. to enforce any
                          provision of these Terms shall not be construed as a waiver of any
                          right or provision. Any waiver of rights by Nexum S.R.L. must be
                          explicit and in writing to be effective.
                        </p>
                        <p>
                          8.3 Severability: If any provision of these Terms is held to be
                          invalid, illegal, or unenforceable by a competent court or
                          authority, that provision shall be enforced to the maximum extent
                          permissible, and the remaining provisions of the Terms will remain
                          in full force and effect. The parties shall negotiate in good
                          faith a valid, legal, and enforceable substitute provision that
                          most nearly reflects the original intent and economic effect of
                          the invalid provision.
                        </p>
                        <p>
                          8.4 Entire Agreement: These Terms (along with the Privacy Policy
                          and any additional agreements for Enterprise subscriptions)
                          constitute the entire agreement between the User and Nexum S.R.L.
                          regarding the Service, superseding any prior agreements or
                          communications (whether written or oral) concerning the same
                          subject matter. The User acknowledges that no reliance is placed
                          on any representation not expressly contained in this Agreement.
                        </p>
                        <p>
                          8.5 Contact Information: For any questions, complaints, or claims
                          regarding the Service or these Terms, Users may contact Nexum
                          S.R.L. at: Nexum S.R.L. – Customer Support Email:
                          nexbotsupport@nexum-ai.com Address: (Insert Registered Office
                          Address, Rome, Italy) Sales Contact: salesnexum@nexum-ai.com (for
                          inquiries regarding Enterprise plans or custom agreements) By
                          purchasing a Nexbot subscription or using the Service, the User
                          confirms that they have read, understood, and agreed to these
                          Terms of Reference. The User further represents that they are of
                          legal age and capacity to enter into this binding Agreement.
                        </p>
                      </section>
                    </div>
                  </div>
                </p>

                <motion.div
                  className="flex items-center space-x-2"
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.button
                    type="button"
                    onClick={() => setIsConfirmationChecked(!isConfirmationChecked)}
                    className={`w-5 h-5 border rounded flex items-center justify-center transition-colors ${isConfirmationChecked
                      ? 'bg-sky-600 border-sky-600'
                      : 'border-gray-300 hover:border-sky-400'
                      }`}
                  >
                    {isConfirmationChecked && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      >
                        <CheckCircle size={16} className="text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                  <span className={`text-sm ${isConfirmationChecked ? 'text-sky-600' : 'text-gray-600 dark:text-gray-300'
                    }`}>
                    Accept terms and conditions
                  </span>
                </motion.div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm   text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={!isConfirmationChecked}
                  onClick={goToCheckout}
                  className={`px-4 py-2 rounded-md text-sm text-white transition-colors ${isConfirmationChecked
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {requestQuoteModal && (
          <RequestQuoteModal
            onClose={handleRequestQuoteClose}
            product={selectedProduct}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PricingPlans;